import { AsyncPipe, isPlatformBrowser, NgClass, NgFor, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
} from '@angular/core';
import { DynamicHooksModule } from 'ngx-dynamic-hooks';
import { debounceTime, fromEvent, Observable, startWith, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { SvgComponent } from 'icon';
import { LineCheckProductsService } from 'line-check-products';
import { ProductBoxComponent } from 'product-box';
import { SwiperComponent, SwiperItemDirective } from 'swiper';
import { TranslatePipe } from 'translate';
import { ProductsTabs } from '../../../enums/products-tabs.enum';
import { ProductsSlice, TabConfig } from './products.slice.class';

interface ProductFilter {
  id: string;
  translationKey: string;
  prismicTitle?: string;
  iconName: string;
}
@Component({
  selector: 'app-products',
  templateUrl: './products.component.html',
  styleUrls: ['./products.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgIf,
    NgFor,
    NgClass,
    SvgComponent,
    SwiperComponent,
    SwiperItemDirective,
    ProductBoxComponent,
    AsyncPipe,
    TranslatePipe,
    DynamicHooksModule,
  ],
})
export class ProductsComponent implements OnInit, OnDestroy {
  private changeDetectorRef = inject(ChangeDetectorRef);
  private platformId = inject(PLATFORM_ID);
  private lineCheckProductsService = inject(LineCheckProductsService);
  @Input() slice: ProductsSlice;
  initialSlideIndex = 0;
  public showProducts$: Observable<boolean>;
  filterTabs: Array<ProductFilter> = [
    { id: ProductsTabs.FEATURED, translationKey: ProductsTabs.FEATURED, iconName: 'small-star' },
    { id: ProductsTabs.LOCAL, translationKey: ProductsTabs.LOCAL, iconName: 'swiss-flag' },
    { id: ProductsTabs.GLOBAL, translationKey: ProductsTabs.GLOBAL, iconName: 'round-globe' },
    { id: ProductsTabs.PREPAID, translationKey: ProductsTabs.PREPAID, iconName: 'sim-card' },
    { id: ProductsTabs.ALL, translationKey: ProductsTabs.ALL, iconName: 'all_icon' },
  ];
  activeFilterTab: ProductFilter = this.filterTabs[0];
  subscription: Subscription;

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      this.subscription = fromEvent(window, 'resize')
        .pipe(debounceTime(500))
        .subscribe(() => {
          this.setInitialSlideIndex();
          this.changeDetectorRef.markForCheck();
        });
    }
  }

  ngOnInit() {
    this.setInitialSlideIndex();
    this.createTabs();
    this.showProducts$ = this.lineCheckProductsService.lineCheckResultsShown$.pipe(
      map(lineCheckResultsShown => !lineCheckResultsShown),
      startWith(true)
    );
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  setInitialSlideIndex() {
    if (
      isPlatformBrowser(this.platformId) &&
      this.slice.mobileStartPosition &&
      this.slice.mobileStartPosition > 0 &&
      this.slice.mobileStartPosition <= this.slice.products.length
    ) {
      const width = window.innerWidth;
      if (width < 768) {
        this.initialSlideIndex = this.slice.mobileStartPosition - 1;
      } else {
        this.initialSlideIndex = 0;
      }
    }
  }

  createTabs() {
    const newTabs = this.filterTabs.map(item => {
      return {
        ...item,
        iconName: this.slice.tabConfig[`${item.id}_tab_icon` as keyof TabConfig]
          ? this.slice.tabConfig[`${item.id}_tab_icon` as keyof TabConfig]
          : item.iconName,
        prismicTitle: this.slice.tabConfig[`${item.id}_tab_title` as keyof TabConfig],
      };
    });
    this.filterTabs = newTabs;
  }

  onProductFilter(productFilter: ProductFilter) {
    this.activeFilterTab = productFilter;
  }

  isActive(productFilter: ProductFilter) {
    return this.activeFilterTab.id === productFilter.id;
  }

  get filteredFilterTabs() {
    // Only show options that are actually present in the possible products.
    // e.g. if no product has 'featured' then don't show that option in the menu
    const allowedFilters = new Set();
    for (const p of this.slice.products) {
      if (p.featured) {
        allowedFilters.add(ProductsTabs.FEATURED);
      }
      if (p.local) {
        allowedFilters.add(ProductsTabs.LOCAL);
      }
      if (p.global) {
        allowedFilters.add(ProductsTabs.GLOBAL);
      }
      if (p.prepaid) {
        allowedFilters.add(ProductsTabs.PREPAID);
      }
      if (p.all) {
        allowedFilters.add(ProductsTabs.ALL);
      }
    }
    return this.filterTabs.filter(f => allowedFilters.has(f.id));
  }

  get filteredProducts() {
    if (this.slice.tabs) {
      const activeFilterId = this.activeFilterTab.id;
      return this.slice.products.filter(p => {
        let allowed = false;
        if (p.featured) {
          allowed = allowed || activeFilterId === ProductsTabs.FEATURED;
        }
        if (p.local) {
          allowed = allowed || activeFilterId === ProductsTabs.LOCAL;
        }
        if (p.global) {
          allowed = allowed || activeFilterId === ProductsTabs.GLOBAL;
        }
        if (p.prepaid) {
          allowed = allowed || activeFilterId === ProductsTabs.PREPAID;
        }
        if (p.all) {
          allowed = allowed || activeFilterId === ProductsTabs.ALL;
        }
        return allowed;
      });
    }
    return this.slice.products;
  }
}
